import React, { useState, useCallback, useMemo, useRef } from 'react';
import { ITableComp } from 'views/components/table/type';
import PushableSideBar from 'views/components/ui/sidebar';
import ConfigurationHeader from 'views/components/configurationHeader';
import TableComponent from 'views/components/table/Table';
import ConfigTableLayout from 'views/components/table/ConfigTableLayout';
import { useHistory } from 'react-router';
import { DT_APPOINTMENT_SUBTYPE_COLUMNS } from 'views/components/table/tableColumns';
import {
  IDTAppointmentSubType,
  DTAppointmentSubType
} from 'model/DTAppointmentSubType';
import { COUNSELING_APPT_SUBTYPE_TITLE } from 'utils/constants/configurations';
import { GET_COUNSELING_APPOINTMENT_SUBTYPES } from 'api/graphql/v2/queries/AppointmentTypes';
import { PUT_COUNSELING_APPOINTMENT_SUBTYPE } from 'api/graphql/v2/mutations/AppointmentTypes';
import { formatCounselingSubTypeToInput } from 'utils/format/appointmentType';
import { EVENT_TYPES } from 'utils/constants/appointmentsTypes';
import { useQuery } from '@apollo/react-hooks';
import Content from 'views/components/ui/content';
import { TableType } from 'model/tableType';
import DTAppointmentSubTypesForm from 'views/components/forms/configurations/DTAppointmentSubType';
import { NEW_DT_APPOINTMENT_SUBTYPE_CONFIG } from 'utils/constants/init';
import { GET_APPT_TYPES_CONFIG } from 'api/graphql/v2/queries/AppointmentTypes';
import { scrollToTop } from 'utils/math';
import {
  ISortValue,
  MAPPER_FUNCTION,
  mapToTableState
} from 'utils/mappers/configuration';
import { IDTAppointmentSubTypeConfig } from 'model/tableConfigurations';
import { EventTypes } from 'model/eventType';
import { GET_SALESFORCE_THERAPY_MAPPINGS } from 'api/graphql/v2/queries/SalesForceTherapyMappings';
import { StatusFilterTabs } from 'components/ui';

type StatusFilter = 'active' | 'inactive' | 'all';

const statusFilterToIsActive = (filter: StatusFilter): boolean | undefined => {
  switch (filter) {
    case 'active':
      return true;
    case 'inactive':
      return false;
    case 'all':
      return undefined;
  }
};

// Counseling subtypes are stored in the DT settings table (scoped on the
// backend to parents whose event type is Counseling), so this screen mirrors
// the DT Appointment SubType configuration.
const CounselingAppointmentSubTypeConfig: React.FC = () => {
  const history = useHistory();
  const [currentForm, setCurrentForm] = useState(-1);
  const [sortValue, setSortValue] = useState<ISortValue>();
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const formValues = useRef(NEW_DT_APPOINTMENT_SUBTYPE_CONFIG);

  const tableHeaders = DT_APPOINTMENT_SUBTYPE_COLUMNS;

  const { loading, error, data } = useQuery(GET_COUNSELING_APPOINTMENT_SUBTYPES, {
    variables: {
      isActive: statusFilterToIsActive(statusFilter)
    },
    fetchPolicy: 'cache-and-network'
  });

  const { data: superTypedata } = useQuery(GET_APPT_TYPES_CONFIG, {
    variables: {
      eventType: EventTypes.Counseling
    },
    fetchPolicy: 'cache-and-network'
  });

  const { data: salesForceData } = useQuery(GET_SALESFORCE_THERAPY_MAPPINGS, {
    fetchPolicy: 'cache-and-network'
  });

  const addNew = useCallback(
    (data?: TableType) => {
      if (data) formValues.current = data as IDTAppointmentSubType;
      else formValues.current = NEW_DT_APPOINTMENT_SUBTYPE_CONFIG;
      scrollToTop();
      setCurrentForm(formValues.current.id!);
    },
    [setCurrentForm]
  );

  const tableData = useMemo(() => {
    return data
      ? (mapToTableState(
          data.appointmentSubTypesConfig,
          addNew,
          MAPPER_FUNCTION.dtAppointmentSubTypes,
          searchValue,
          sortValue
        ) as IDTAppointmentSubTypeConfig[])
      : [];
  }, [data, addNew, searchValue, sortValue]);

  const onSearch = useCallback(
    value => {
      setSearchValue(value);
    },
    [setSearchValue]
  );

  const onSort = useCallback(
    (comp: ITableComp, reverse: boolean) => {
      setSortValue({ comp, reverse });
    },
    [setSortValue]
  );

  const onFormClose = useCallback(() => {
    formValues.current = NEW_DT_APPOINTMENT_SUBTYPE_CONFIG;
    setCurrentForm(-1);
  }, [setCurrentForm]);

  const onGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleStatusFilterChange = useCallback((value: StatusFilter) => {
    setStatusFilter(value);
  }, []);

  return (
    <>
      <PushableSideBar
        content={
          <DTAppointmentSubTypesForm
            title={
              formValues.current.id
                ? `Edit ${COUNSELING_APPT_SUBTYPE_TITLE}`
                : `New ${COUNSELING_APPT_SUBTYPE_TITLE}`
            }
            superTypeList={superTypedata?.appointmentTypesConfig}
            salesForceData={salesForceData?.getSFTherapyMappings}
            initialValues={formValues.current}
            onFormClose={onFormClose}
            currentStatusFilter={statusFilterToIsActive(statusFilter)}
            mutationDoc={PUT_COUNSELING_APPOINTMENT_SUBTYPE}
            refetchQueryDoc={GET_COUNSELING_APPOINTMENT_SUBTYPES}
            eventType={EVENT_TYPES.Counseling}
            formatToInput={formatCounselingSubTypeToInput}
          />
        }
        visible={currentForm !== -1}
        pageComponent={
          <Content
            loading={loading}
            error={error}
            data={data}
            fullHeight={true}
          >
            {() => (
              <ConfigTableLayout>
                <ConfigurationHeader
                  addNew={addNew}
                  onSearch={onSearch}
                  title={COUNSELING_APPT_SUBTYPE_TITLE}
                  onClick={onGoBack}
                  visible={currentForm !== -1}
                />
                <StatusFilterTabs
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                />
                <TableComponent
                  headers={tableHeaders}
                  data={tableData}
                  sort={onSort}
                  primarySortedKey={DTAppointmentSubType.superType}
                />
              </ConfigTableLayout>
            )}
          </Content>
        }
      />
    </>
  );
};

export default CounselingAppointmentSubTypeConfig;
